import { Box, Card, Divider, DuoColorIcon, Icon, Table } from '@zillow/constellation-10';
import { IconDollarSignCircleFilled } from '@zillow/constellation-icons';
import { LoadingButton, LoadingHeading, LoadingTag, LoadingText } from '@zg-rentals/loading-components';

type TransactionsTableSkeletonProps = {
  isDesktop?: boolean;
};

export const TransactionsTableSkeleton = ({ isDesktop = false }: TransactionsTableSkeletonProps) => {
  const header = (
    <Box
      css={{
        display: 'flex',
        alignItems: 'center',
        gap: 'tighter',
        paddingBottom: 'default',
        ...(isDesktop && {
          borderBottom: '{borderWidths.default} solid {colors.gray.300}',
        }),
        width: '100%',
      }}
    >
      <DuoColorIcon tone="info" css={{ opacity: 0.3 }}>
        <Icon>
          <IconDollarSignCircleFilled />
        </Icon>
      </DuoColorIcon>
      <LoadingHeading isLoading level={1} textStyle="heading-sm">
        Loading
      </LoadingHeading>
    </Box>
  );

  if (isDesktop) {
    return (
      <Card
        elevated={false}
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '{spacing.default} {spacing.loose} {spacing.loose}',
        }}
        data-testid="TransactionsTableSkeleton-Desktop"
      >
        {header}
        <Box
          css={{
            width: '100%',
          }}
        >
          <Table.Root
            alignments={['start', 'start', 'start', 'end']}
            appearance="horizontal"
            size="md"
            aria-label="Loading payments transactions table"
          >
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  <LoadingText isLoading>Loading</LoadingText>
                </Table.HeaderCell>
                <Table.HeaderCell>
                  <LoadingText isLoading>Loading</LoadingText>
                </Table.HeaderCell>
                <Table.HeaderCell>
                  <LoadingText isLoading>Loading</LoadingText>
                </Table.HeaderCell>
                <Table.HeaderCell>&nbsp;</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {[1, 2, 3].map((index) => (
                <Table.Row key={index}>
                  <Table.Cell>
                    <LoadingText isLoading>Loading</LoadingText>
                  </Table.Cell>
                  <Table.Cell>
                    <LoadingTag isLoading loadingWidth="50%">
                      Tag
                    </LoadingTag>
                  </Table.Cell>
                  <Table.Cell>
                    <LoadingText isLoading>Loading</LoadingText>
                  </Table.Cell>
                  <Table.Cell>
                    <LoadingButton isLoading loadingWidth="25%" size="sm">
                      Action
                    </LoadingButton>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      </Card>
    );
  }

  // Mobile view
  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 'loose',
      }}
      data-testid="TransactionsTableSkeleton-Mobile"
    >
      {header}
      <Box
        css={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {[1, 2, 3].map((index) => (
          <Box key={index} css={{ display: 'flex', flexDirection: 'column' }}>
            <Box
              css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBlock: 'tight' }}
            >
              <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter', flex: 1 }}>
                <LoadingTag isLoading loadingWidth="30%">
                  Tag
                </LoadingTag>
                <LoadingText isLoading loadingWidth="75%">
                  Loading
                </LoadingText>
              </Box>
              <LoadingButton isLoading loadingWidth="20%" size="sm">
                Action
              </LoadingButton>
            </Box>
            {index < 3 && <Divider />}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
